import React, { useState, useRef, useEffect } from 'react';
import { Building2, Calendar, User, LogOut, ChevronDown, Shield, Key, Moon, Sun, Download, Upload } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { useConfirm } from '../../context/ConfirmContext';
import { useTheme } from '../../context/ThemeContext';
import { getTenantId } from '../../tenant';
import { today } from '../../utils/helpers';
import Modal from '../common/Modal';

export default function Header({ onNavigate, onToggleSidebar }) {
  const { state, resetData, dispatch } = useApp();
  const { currentUser, logout } = useAuth();
  const confirm = useConfirm();
  const { dark, toggleTheme } = useTheme();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwords, setPasswords] = useState({ current: '', newPass: '', confirm: '' });
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState(false);
  const [showBackupReminder, setShowBackupReminder] = useState(false);
  const menuRef = useRef(null);

  // Remind admins to back up if it's been > 7 days (snoozable for a day).
  const LAST_BACKUP_KEY = `erp_last_backup_${getTenantId()}`;
  const BACKUP_SNOOZE_KEY = `erp_backup_snooze_${getTenantId()}`;
  useEffect(() => {
    if (currentUser?.role !== 'Admin') { setShowBackupReminder(false); return; }
    try {
      const now = Date.now();
      const last = localStorage.getItem(LAST_BACKUP_KEY);
      const snooze = localStorage.getItem(BACKUP_SNOOZE_KEY);
      const overdue = !last || (now - new Date(last).getTime()) > 7 * 24 * 3600 * 1000;
      const snoozed = snooze && (now - new Date(snooze).getTime()) < 24 * 3600 * 1000;
      setShowBackupReminder(overdue && !snoozed);
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);
  const { updateUser } = useAuth();

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleLogout = async () => {
    const ok = await confirm('Are you sure you want to log out?', { title: 'Logout', confirmLabel: 'Logout' });
    if (ok) logout();
  };

  // Download a full JSON backup of this company's data. The browser's save
  // dialog lets you choose where to keep it.
  const handleBackup = () => {
    try {
      const backup = { app: 'MayaSoft ERP', tenant: getTenantId(), exportedAt: new Date().toISOString(), data: state };
      const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mayasoft-backup-${getTenantId()}-${today()}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      try { localStorage.setItem(LAST_BACKUP_KEY, new Date().toISOString()); } catch { /* ignore */ }
      setShowBackupReminder(false);
    } catch (e) {
      alert('Backup failed: ' + e.message);
    }
  };

  const remindBackupLater = () => {
    try { localStorage.setItem(BACKUP_SNOOZE_KEY, new Date().toISOString()); } catch { /* ignore */ }
    setShowBackupReminder(false);
  };

  // Restore from a previously downloaded JSON backup (replaces current data).
  const restoreInputRef = useRef(null);
  const handleRestoreFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const parsed = JSON.parse(reader.result);
        const data = parsed && parsed.data ? parsed.data : parsed; // accept wrapped or raw state
        if (!data || !data.company) { alert('This file does not look like a MayaSoft backup.'); return; }
        const ok = await confirm('Restore this backup? It will REPLACE all current data for this company. (Tip: download a fresh backup first.)', { title: 'Restore Backup', variant: 'danger', confirmLabel: 'Restore' });
        if (!ok) return;
        dispatch({ type: 'LOAD_STATE', payload: data });
        alert('Backup restored successfully.');
      } catch (err) {
        alert('Restore failed: ' + err.message);
      }
    };
    reader.readAsText(file);
  };

  const handleChangePassword = () => {
    setPwError('');
    if (!passwords.current) { setPwError('Current password is required'); return; }
    if (passwords.current !== currentUser.password) { setPwError('Current password is incorrect'); return; }
    if (!passwords.newPass) { setPwError('New password is required'); return; }
    if (passwords.newPass.length < 6) { setPwError('New password must be at least 6 characters'); return; }
    if (passwords.newPass !== passwords.confirm) { setPwError('Passwords do not match'); return; }
    if (passwords.current === passwords.newPass) { setPwError('New password must be different from current'); return; }

    const result = updateUser(currentUser.id, { password: passwords.newPass });
    if (result.success) {
      setPwSuccess(true);
      setTimeout(() => {
        setShowChangePassword(false);
        setPasswords({ current: '', newPass: '', confirm: '' });
        setPwSuccess(false);
      }, 1500);
    } else {
      setPwError(result.error);
    }
  };

  return (
    <>
      <header className="bg-white border-b border-gray-200 px-4 md:px-6 py-3 flex items-center justify-between no-print">
        <div className="flex items-center gap-2">
          <button
            onClick={onToggleSidebar}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-500 md:hidden"
            aria-label="Toggle sidebar"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
          </button>
          <button
            onClick={() => onNavigate?.('business-profile')}
            className="flex items-center gap-3 hover:bg-gray-50 rounded-lg px-2 py-1 -ml-2 transition-colors group"
          title="Edit Business Profile"
        >
          {state.company.logo ? (
            <img src={state.company.logo} alt="Logo" className="w-9 h-9 object-contain rounded" />
          ) : (
            <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200 transition-colors">
              <Building2 size={20} className="text-blue-600" />
            </div>
          )}
          <div className="text-left">
            <h1 className="text-lg font-bold text-gray-900 group-hover:text-blue-700 transition-colors">{state.company.name}</h1>
            <p className="text-xs text-gray-500">
              {[state.company.city, state.company.state].filter(Boolean).join(', ') || state.company.address}
              {state.company.gstin && <span className="ml-2 text-gray-400">| GSTIN: {state.company.gstin}</span>}
            </p>
          </div>
        </button>
        </div>

        <div className="flex items-center gap-2 md:gap-4">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Calendar size={16} />
            <span>FY {state.company.financialYear}</span>
          </div>

          {/* Dark mode toggle */}
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-700"
            title={dark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          >
            {dark ? <Sun size={18} /> : <Moon size={18} />}
          </button>

          {/* User dropdown */}
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <div className="w-7 h-7 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                {currentUser?.fullName?.charAt(0)?.toUpperCase() || 'A'}
              </div>
              <div className="text-left hidden sm:block">
                <p className="text-sm font-medium text-gray-700">{currentUser?.fullName || 'Admin'}</p>
                <p className="text-[10px] text-gray-400">{currentUser?.role || 'Admin'}</p>
              </div>
              <ChevronDown size={14} className="text-gray-400" />
            </button>

            {showUserMenu && (
              <div className="absolute right-0 top-full mt-1 w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-50">
                <div className="px-4 py-3 border-b border-gray-100">
                  <p className="text-sm font-semibold text-gray-900">{currentUser?.fullName}</p>
                  <p className="text-xs text-gray-500">@{currentUser?.username}</p>
                  <span className="inline-flex items-center gap-1 mt-1 text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                    <Shield size={10} /> {currentUser?.role}
                  </span>
                </div>

                <button
                  onClick={() => { setShowUserMenu(false); setShowChangePassword(true); }}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <Key size={16} className="text-gray-400" /> Change Password
                </button>

                {currentUser?.role === 'Admin' && (
                  <button
                    onClick={() => { setShowUserMenu(false); onNavigate?.('user-management'); }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <User size={16} className="text-gray-400" /> User Management
                  </button>
                )}

                <div className="border-t border-gray-100 mt-1 pt-1">
                  {currentUser?.role === 'Admin' && (
                    <button
                      onClick={() => { setShowUserMenu(false); handleBackup(); }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <Download size={16} className="text-gray-400" /> Download Backup
                    </button>
                  )}
                  {currentUser?.role === 'Admin' && (
                    <button
                      onClick={() => { setShowUserMenu(false); restoreInputRef.current?.click(); }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <Upload size={16} className="text-gray-400" /> Restore Backup
                    </button>
                  )}
                  {currentUser?.role === 'Admin' && (
                    <button
                      onClick={async () => {
                        const ok = await confirm('Reset all data to defaults? This cannot be undone.', { title: 'Reset Data', variant: 'danger', confirmLabel: 'Reset' });
                        if (ok) {
                          resetData();
                          window.location.reload();
                        }
                      }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-500 hover:bg-gray-50 transition-colors"
                    >
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                      Reset Data
                    </button>
                  )}
                  <button
                    onClick={() => { setShowUserMenu(false); handleLogout(); }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <LogOut size={16} /> Sign Out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {showBackupReminder && (
        <div className="no-print bg-amber-50 border-b border-amber-200 px-4 py-2 flex flex-wrap items-center justify-between gap-2 text-sm">
          <span className="text-amber-800 flex items-center gap-2">
            <Download size={15} /> It's been a while since your last backup. Keep your data safe — download a backup.
          </span>
          <div className="flex gap-2">
            <button onClick={handleBackup} className="btn btn-primary btn-sm flex items-center gap-1.5"><Download size={14} /> Download Backup</button>
            <button onClick={remindBackupLater} className="btn btn-secondary btn-sm">Remind me later</button>
          </div>
        </div>
      )}

      <input ref={restoreInputRef} type="file" accept="application/json,.json" className="hidden" onChange={handleRestoreFile} />

      {/* Change Password Modal */}
      <Modal isOpen={showChangePassword} onClose={() => { setShowChangePassword(false); setPasswords({ current: '', newPass: '', confirm: '' }); setPwError(''); setPwSuccess(false); }} title="Change Password">
        {pwSuccess ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
            </div>
            <p className="text-lg font-semibold text-gray-900">Password Changed!</p>
            <p className="text-sm text-gray-500">Your password has been updated successfully.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {pwError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{pwError}</div>
            )}
            <div>
              <label className="label">Current Password *</label>
              <input className="input" type="password" value={passwords.current} onChange={e => setPasswords({...passwords, current: e.target.value})} autoFocus />
            </div>
            <div>
              <label className="label">New Password *</label>
              <input className="input" type="password" value={passwords.newPass} onChange={e => setPasswords({...passwords, newPass: e.target.value})} placeholder="Minimum 6 characters" />
            </div>
            <div>
              <label className="label">Confirm New Password *</label>
              <input className="input" type="password" value={passwords.confirm} onChange={e => setPasswords({...passwords, confirm: e.target.value})} />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => { setShowChangePassword(false); setPasswords({ current: '', newPass: '', confirm: '' }); setPwError(''); }} className="btn btn-secondary">Cancel</button>
              <button onClick={handleChangePassword} className="btn btn-primary">Change Password</button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}
